import React, { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import AnimatedCounter from '@/components/apex/AnimatedCounter';
import { Button } from '@/components/ui/button';
import AuditBundles from '@/components/apex/AuditBundles';
import RecordDrawer from '@/components/apex/RecordDrawer';

const STATUS_COLOR = {
  evidence_assembled: { bg: 'bg-emerald-50', text: 'text-apex-success', label: 'EVIDENCE ASSEMBLED' },
  in_progress: { bg: 'bg-amber-50', text: 'text-apex-warning', label: 'IN PROGRESS' },
};

const SHORTCUT_DATES = {
  'Mar 15 — SOC 2 audit cutoff': '2026-03-15',
  'Apr 14 — INV-2026-042 trigger': '2026-04-14',
  'Apr 12 — last gap start': '2026-04-12',
  'Q1 close': '2026-03-31',
};

export default function AuditWorkspace() {
  const { seed } = useApp();
  const navigate = useNavigate();
  const [activeFwk, setActiveFwk] = useState(null);
  const [activeReq, setActiveReq] = useState(null);
  if (!seed) return null;
  const a = seed.audit_workspace;

  const buildFrameworkRecord = (f) => ({
    idTag: f.name,
    title: `${f.name} readiness`,
    severity: f.gaps === 0 ? 'enforced' : f.gaps > 2 ? 'high' : 'medium',
    subtitle: `${f.readiness_pct}% control coverage · next audit ${f.next_audit}`,
    meta: [
      { label: 'Readiness', value: `${f.readiness_pct}%`, mono: true, tone: f.readiness_pct >= 90 ? 'success' : 'warning' },
      { label: 'Open gaps', value: f.gaps, mono: true, tone: f.gaps === 0 ? 'success' : 'critical' },
      { label: 'Next audit', value: f.next_audit, mono: true },
      { label: 'Auditor', value: f.auditor ?? 'TBD', mono: true },
    ],
    sections: [
      {
        title: 'What this score means',
        type: 'paragraph',
        body: `Readiness is the share of in-scope ${f.name} controls that have a current signed evidence packet within the framework's required cadence. APEX recomputes this every time new CloudTrail or Config evidence arrives.`,
      },
      {
        title: 'Path to 100%',
        type: 'list',
        items: [
          'Open the Evidence Vault and pull a packet for each open gap',
          'Pin recurring evidence to a bundle so the next audit cycle is one click',
          'Investigations linked to gaps will auto-close their contribution once verified',
        ],
      },
    ],
    actions: [
      { label: 'Open Evidence Vault', onClick: () => navigate('/lens/evidence'), primary: true },
      { label: 'Open Investigations', onClick: () => navigate('/lens/investigations') },
    ],
  });

  const buildRequestRecord = (r) => ({
    idTag: r.id,
    title: `${r.framework} · Control ${r.control}`,
    severity: r.status === 'evidence_assembled' ? 'enforced' : 'medium',
    subtitle: `${r.auditor} · Due ${r.due}`,
    meta: [
      { label: 'Framework', value: r.framework },
      { label: 'Control', value: r.control, mono: true },
      { label: 'Auditor', value: r.auditor },
      { label: 'Due', value: r.due, mono: true },
      { label: 'Status', value: STATUS_COLOR[r.status]?.label, tone: r.status === 'evidence_assembled' ? 'success' : 'warning' },
    ],
    sections: [
      {
        title: 'What the auditor will see',
        type: 'list',
        items: [
          'Signed evidence packet with the relevant CloudTrail and Config records',
          'Hash chain proving the packet has not been modified since generation',
          'Control mapping back to the framework with each event tagged to its requirement',
          'Reconstruction snapshot at the audit-cutoff date (one click from this page)',
        ],
      },
      {
        title: 'Why this matters',
        type: 'paragraph',
        body: 'Open audit requests block close-out for the framework. APEX automatically assembles the evidence the moment the request is created — auditors get framework-mapped packets on demand without a single follow-up email.',
      },
    ],
    actions: [
      { label: 'Open packet', onClick: () => navigate('/lens/evidence'), primary: true },
      { label: 'Open Reconstruction', onClick: () => navigate(`/lens/reconstruction?date=${r.due}`) },
    ],
  });

  return (
    <div className="space-y-6" data-testid="audit-workspace">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Evidence · Audit Workspace</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Framework readiness, end to end</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {a.frameworks.map((f, i) => (
          <motion.button
            key={f.name}
            type="button"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            onClick={() => setActiveFwk(f)}
            data-testid={`audit-framework-${f.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className="bg-white rounded-lg border border-slate-200 p-4 text-left hover:shadow-sm hover:-translate-y-0.5 hover:border-apex-lens transition-all"
          >
            <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{f.name}</div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-3xl font-bold tabular text-apex-ink"><AnimatedCounter value={f.readiness_pct} suffix="%" /></span>
            </div>
            <div className="text-[11px] text-apex-neutral mt-1">{f.gaps} open gap{f.gaps !== 1 ? 's' : ''}</div>
            <div className="text-[11px] text-apex-neutral mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" />Next audit: {f.next_audit}</div>
          </motion.button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-bold text-apex-ink">Open Audit Requests</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {a.open_requests.map((r) => {
            const s = STATUS_COLOR[r.status];
            return (
              <div
                key={r.id}
                onClick={() => setActiveReq(r)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveReq(r); } }}
                data-testid={`audit-request-${r.id}`}
                className="p-4 flex flex-wrap items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-apex-neutral">{r.id}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${s.text}`}>{s.label}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-apex-ink">{r.framework} · Control {r.control}</h4>
                  <p className="text-[11px] text-apex-neutral mt-0.5">{r.auditor} · Due {r.due}</p>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Button
                    onClick={() => navigate('/lens/evidence')}
                    data-testid={`audit-open-packet-${r.id}`}
                    className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5"
                  >
                    Open packet
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AuditBundles />

      <div className="bg-apex-shell text-white rounded-xl p-5 grain">
        <h3 className="text-sm font-bold">Reconstruction Shortcuts</h3>
        <p className="text-xs text-slate-400 mt-1">Audit dates, materially relevant moments, and exposure boundaries — one click.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {['Mar 15 — SOC 2 audit cutoff', 'Apr 14 — INV-2026-042 trigger', 'Apr 12 — last gap start', 'Q1 close'].map((p) => (
            <button
              key={p}
              onClick={() => navigate(`/lens/reconstruction?date=${SHORTCUT_DATES[p]}`)}
              data-testid={`audit-shortcut-${SHORTCUT_DATES[p]}`}
              className="text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-slate-300 hover:border-apex-lens hover:text-apex-lens transition-colors"
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <RecordDrawer
        open={!!activeFwk}
        onOpenChange={(o) => !o && setActiveFwk(null)}
        record={activeFwk ? buildFrameworkRecord(activeFwk) : null}
        testId="audit-framework-drawer"
      />
      <RecordDrawer
        open={!!activeReq}
        onOpenChange={(o) => !o && setActiveReq(null)}
        record={activeReq ? buildRequestRecord(activeReq) : null}
        testId="audit-request-drawer"
      />
    </div>
  );
}
